import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/auth'
import { LoadingState } from './components/common'
import { Layout } from './components/layout'
import { Login, ForgotPassword, ResetPassword, AcceptInvite } from './pages'
import { createLogger } from './utils/logger'
import { canOpenPath, homePath } from './utils/navigation'

const log = createLogger('App')

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Cores = lazy(() => import('./pages/Cores'))
const CoreDetail = lazy(() => import('./pages/CoreDetail'))
const Events = lazy(() => import('./pages/Events'))
const Users = lazy(() => import('./pages/TenantUsers'))
const Settings = lazy(() => import('./pages/Settings'))
const CloudTenants = lazy(() => import('./pages/CloudTenants'))
const Audit = lazy(() => import('./pages/Audit'))
const Sites = lazy(() => import('./pages/Sites'))

/**
 * AuthInitializer
 *
 * Authentication tokens are intentionally memory-only. A browser reload starts
 * a new session instead of restoring a bearer credential from web storage.
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { isInitialized, setInitialized } = useAuthStore()

  useEffect(() => {
    log.debug('memory-only session initialized')
    setInitialized()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isInitialized) {
    return (
      <div className="login-page">
        <LoadingState message="Initializing..." />
      </div>
    )
  }

  return <>{children}</>
}

/**
 * ProtectedRoute
 *
 * Checks if the user is authenticated. If not, redirects to /login
 * with a return URL so the user can be sent back after logging in.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isInitialized } = useAuthStore()
  const location = useLocation()

  if (!isInitialized) {
    return <LoadingState message="Loading..." />
  }

  if (!user) {
    const returnUrl = location.pathname + location.search
    return <Navigate to={`/login?return=${encodeURIComponent(returnUrl)}`} replace />
  }

  return <>{children}</>
}

/**
 * RoleRoute
 *
 * Opens a page only for a role the sidebar would offer it to (canOpenPath in
 * utils/navigation.ts); any other role goes to its home. The API enforces the
 * same roles; this keeps a typed address from rendering a page that can only
 * fail.
 */
function RoleRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role)
  const { pathname } = useLocation()
  return canOpenPath(pathname, role) ? <>{children}</> : <Navigate to={homePath(role)} replace />
}

function HomeRoute() {
  const role = useAuthStore((s) => s.user?.role)
  return <Navigate to={homePath(role)} replace />
}

/**
 * SuspenseWrapper
 *
 * Wraps lazy-loaded pages in a Suspense boundary with a loading fallback.
 */
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingState message="Loading page..." />}>
      {children}
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthInitializer>
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Protected routes with layout */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRoute />} />
          <Route path="dashboard" element={<RoleRoute><SuspenseWrapper><Dashboard /></SuspenseWrapper></RoleRoute>} />
          <Route path="cores" element={<RoleRoute><SuspenseWrapper><Cores /></SuspenseWrapper></RoleRoute>} />
          <Route path="cores/:coreId" element={<RoleRoute><SuspenseWrapper><CoreDetail /></SuspenseWrapper></RoleRoute>} />
          <Route path="sites" element={<RoleRoute><SuspenseWrapper><Sites /></SuspenseWrapper></RoleRoute>} />
          <Route path="events" element={<RoleRoute><SuspenseWrapper><Events /></SuspenseWrapper></RoleRoute>} />
          <Route path="users" element={<RoleRoute><SuspenseWrapper><Users /></SuspenseWrapper></RoleRoute>} />
          <Route path="settings" element={<RoleRoute><SuspenseWrapper><Settings /></SuspenseWrapper></RoleRoute>} />
          <Route path="tenants" element={<RoleRoute><SuspenseWrapper><CloudTenants /></SuspenseWrapper></RoleRoute>} />
          <Route path="audit" element={<RoleRoute><SuspenseWrapper><Audit /></SuspenseWrapper></RoleRoute>} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<HomeRoute />} />
      </Routes>
    </AuthInitializer>
  )
}
